/**
 * src/components/pages/AdminPage.js
 *
 * Panel de administración de UBIYAAA.
 *
 * Flujo:
 *   1. Login con contraseña (sessionStorage)
 *   2. Carga businesses.json y promos.json via fetch
 *   3. El admin edita, crea o elimina registros (en memoria)
 *   4. "Exportar JSON" descarga el archivo actualizado
 *   5. El admin sube el archivo a Hostinger (no hace falta recompilar)
 *
 * Los datos editados se guardan también en localStorage como respaldo,
 * para no perder cambios si se cierra el tab accidentalmente.
 */

import '@/styles/admin.css'
import { define, c } from '@components/Component.js'
import { h } from '@core/dom.js'
import { fetchData, clearCache } from '@/services/api.js'
import { downloadJson, showToast, nextId } from '@components/admin/adminHelpers.js'
import { publishFiles, reconfigureGithub } from '@components/admin/adminPublish.js'
import { cleanWhatsapp, normalizeSocial } from '@/shared/utils/whatsapp.js'

const LS_BIZ    = 'adm-businesses'
const LS_PROMO  = 'adm-promos'
const LS_SYNCED = 'adm-synced'

define('AdminPage', () => {

    /* ── Estado de autenticación ─────────────────────────── */
    const isAuth = !!sessionStorage.getItem('admin-auth')
    if (!isAuth) {
        // Renderiza login y cuando el usuario entra, recarga la página
        return c('AdminLogin', {
            onSuccess: () => window.location.reload()
        })
    }

    /* ── Contenedor principal ────────────────────────────── */
    const root = h('div', { className: 'admin' })

    /* ── Topbar ──────────────────────────────────────────── */
    const logoutBtn = h('button', { className: 'adm-btn adm-btn--ghost', style: 'color:#fff;border-color:rgba(255,255,255,.3)' },
        '🚪 Cerrar sesión'
    )
    logoutBtn.addEventListener('click', () => {
        sessionStorage.removeItem('admin-auth')
        window.location.href = '/'
    })

    const homeLink = h('a', { href: '/', className: 'adm-btn adm-btn--ghost', style: 'color:#fff;border-color:rgba(255,255,255,.3);text-decoration:none' },
        '🌐 Ver sitio'
    )

    const ghBtn = h('button', { className: 'adm-btn adm-btn--ghost', style: 'color:#fff;border-color:rgba(255,255,255,.3)' },
        '🐙 GitHub'
    )
    ghBtn.addEventListener('click', reconfigureGithub)

    root.appendChild(
        h('div', { className: 'admin-topbar' },
            h('div', { className: 'admin-topbar__brand' },
                'UBIYAAA',
                h('span', {}, '/ Admin')
            ),
            h('div', { className: 'admin-topbar__actions' }, homeLink, ghBtn, logoutBtn)
        )
    )

    /* ── Cuerpo ──────────────────────────────────────────── */
    const body = h('div', { className: 'admin-body' })
    root.appendChild(body)

    /* ── Estado (cargando / error / listo) ───────────────── */
    let businesses = []
    let promos     = []
    let bizMeta    = {}
    let activeTab  = 'businesses'

    /* ── Cargar datos ────────────────────────────────────── */
    async function loadData() {
        body.innerHTML = '<p style="padding:2rem;color:#64748b">⏳ Cargando datos…</p>'

        try {
            // Intentar restaurar desde localStorage (cambios no exportados)
            const savedBiz   = localStorage.getItem(LS_BIZ)
            const savedPromo = localStorage.getItem(LS_PROMO)

            if (savedBiz) {
                businesses = JSON.parse(savedBiz)
            } else {
                const data = await fetchData('businesses.json')
                businesses = data.items
            }

            // bizMeta siempre se carga del servidor para no perder section/tags al publicar
            const promoServer = await fetchData('promos.json')
            bizMeta = { section: promoServer.section, tags: promoServer.tags, tagColors: promoServer.tagColors }

            if (savedPromo) {
                promos = JSON.parse(savedPromo)
            } else {
                promos = promoServer.items
            }

            if ((savedBiz || savedPromo) && !localStorage.getItem(LS_SYNCED)) {
                showToast('📋 Se restauraron cambios pendientes de publicar')
            }

            render()
        } catch (err) {
            body.innerHTML = `<p style="padding:2rem;color:#ef4444">❌ Error al cargar datos: ${err.message}</p>`
        }
    }

    /* ── Guardar en localStorage ─────────────────────────── */
    function persist() {
        localStorage.setItem(LS_BIZ,   JSON.stringify(businesses))
        localStorage.setItem(LS_PROMO, JSON.stringify(promos))
        localStorage.removeItem(LS_SYNCED)
    }

    /* ── Exportar JSON ───────────────────────────────────── */
    function exportBusinesses() {
        downloadJson({ items: businesses }, 'businesses')
        showToast('⬇️ businesses.json descargado. Súbelo a Hostinger.')
    }
    function exportPromos() {
        const data = { ...bizMeta, items: promos }
        downloadJson(data, 'promos')
        showToast('⬇️ promos.json descargado. Súbelo a Hostinger.')
    }
    /* ── Publicar directo en GitHub ──────────────────────── */
    async function publishAll(btn) {
        btn.disabled = true
        btn.textContent = '⏳ Publicando…'
        try {
            // Un único commit atómico con ambos archivos → un solo deploy.
            // Evita la carrera entre dos workflows que pisaba los cambios.
            await publishFiles([
                { path: 'public/data/businesses.json', data: { items: businesses } },
                { path: 'public/data/promos.json',     data: { ...bizMeta, items: promos } },
            ], 'contenido: actualizar negocios y promos desde el admin')

            localStorage.removeItem(LS_BIZ)
            localStorage.removeItem(LS_PROMO)
            localStorage.removeItem(LS_SYNCED)
            clearCache()
            showToast('🚀 Publicado. El sitio se actualiza en ~2 minutos.', 5000)
            render()
        } catch (err) {
            showToast(`❌ ${err.message}`, 5000)
            btn.disabled = false
            btn.textContent = '🚀 Publicar en el sitio'
        }
    }

    /* ── Importar solicitud pública (JSON de /registrar) ──── */
    function importRequest(json) {
        let data
        try {
            data = typeof json === 'string' ? JSON.parse(json) : json
        } catch {
            showToast('❌ El archivo no es un JSON válido', 4000)
            return
        }

        if (data._solId) {
            const alreadyBiz = businesses.some(b => b._solId === data._solId)
            const alreadyPro = promos.some(p => p._solId === data._solId)
            if (alreadyBiz || alreadyPro) {
                showToast('⚠️ Esta solicitud ya fue importada', 3000)
                return
            }
        }

        if (data._type === 'promo') {
            const promo = {
                id:          nextId(promos),
                business:    data.business || '',
                businessId:  null,
                title:       data.title || '',
                tag:         data.tag || 'Descuento',
                description: data.description || '',
                image:       data.image || '',
                expires:     data.expires || null,
                responsable: data.responsable || null,
                _solId:      data._solId || null,
                enabled:     false,
            }
            promos.push(promo)
            persist()
            activeTab = 'promos'
            render()
            showToast(`📥 Promoción "${promo.title}" importada (deshabilitada). Revísala y actívala.`, 5000)
        } else {
            const biz = {
                id:              nextId(businesses),
                plan:            'oro',
                name:            data.name || '',
                category:        data.category || '',
                categorySlug:    data.categorySlug || '',
                description:     data.description || '',
                fullDescription: data.fullDescription || '',
                logo:            data.logo || '',
                image:           data.image || '',
                video:           data.video || '',
                gallery:         Array.isArray(data.gallery) ? data.gallery : [],
                catalog:         data.catalog || { url: '', label: '', description: '' },
                location:        data.location || '',
                mapLabel:        data.mapLabel || data.location || '',
                mapUrl:          '',
                mapEmbed:        '',
                whatsapp:        cleanWhatsapp(data.whatsapp),
                social:          normalizeSocial(data.social),
                rating:          0,
                reviews:         0,
                badge:           null,
                badgeColor:      null,
                open:            true,
                hasPromo:        false,
                tags:            Array.isArray(data.tags) ? data.tags : [],
                responsable:     data.responsable || null,
                _solId:          data._solId || null,
                enabled:         false,
                activeFrom:      null,
                activeTo:        null,
            }
            businesses.push(biz)
            persist()
            activeTab = 'businesses'
            render()
            showToast(`📥 Negocio "${biz.name}" importado (deshabilitado). Revísalo y actívalo.`, 5000)
        }
    }

    function discardChanges() {
        if (!confirm('¿Descartar todos los cambios y recargar desde el servidor?')) return
        localStorage.removeItem(LS_BIZ)
        localStorage.removeItem(LS_PROMO)
        localStorage.removeItem(LS_SYNCED)
        clearCache()
        loadData()
    }

    /* ── Operaciones sobre negocios ──────────────────────── */
    function bizSave(biz) {
        const idx = businesses.findIndex(b => b.id === biz.id)
        if (idx >= 0) businesses[idx] = biz
        else          businesses.push(biz)
        persist()
        render()
    }
    function bizDelete(id) {
        businesses = businesses.filter(b => b.id !== id)
        persist()
        render()
    }
    function bizToggle(id, enabled) {
        businesses = businesses.map(b => b.id === id ? { ...b, enabled } : b)
        persist()
        render()
    }
    function bizViewsBoost(id, views) {
        businesses = businesses.map(b => b.id === id ? { ...b, views } : b)
        persist()
    }

    /* ── Operaciones sobre promos ────────────────────────── */
    function promoSave(promo) {
        const idx = promos.findIndex(p => p.id === promo.id)
        if (idx >= 0) promos[idx] = promo
        else          promos.push(promo)
        persist()
        render()
    }
    function promoDelete(id) {
        promos = promos.filter(p => p.id !== id)
        persist()
        render()
    }
    function promoToggle(id, enabled) {
        promos = promos.map(p => p.id === id ? { ...p, enabled } : p)
        persist()
        render()
    }
    function promoViewsBoost(id, views) {
        promos = promos.map(p => p.id === id ? { ...p, views } : p)
        persist()
    }

    /* ── Render del panel ────────────────────────────────── */
    function render() {
        body.innerHTML = ''

        /* Indicador de cambios pendientes:
           hay datos locales Y no han sido publicados todavía */
        const hasPending = (!!localStorage.getItem(LS_BIZ) || !!localStorage.getItem(LS_PROMO))
                        && !localStorage.getItem(LS_SYNCED)

        /* Barra de exportación */
        const exportBar = h('div', { className: 'adm-export-bar' },
            h('div', { className: 'adm-export-bar__text' },
                hasPending
                    ? h('span', {}, '⚠️ Tienes cambios sin publicar. ', h('strong', {}, 'Pulsa 🚀 Publicar para que salgan al sitio (~2 min).'))
                    : h('span', {}, '✅ Sin cambios pendientes. Los datos publicados están al día.')
            ),
            h('div', { className: 'adm-export-btns' },
                (() => {
                    const b = h('button', { className: 'adm-btn adm-btn--primary', type: 'button' }, '🚀 Publicar en el sitio')
                    b.addEventListener('click', () => publishAll(b))
                    return b
                })(),
                (() => {
                    const fileInput = h('input', { type: 'file', accept: 'application/json,.json', style: 'display:none' })
                    fileInput.addEventListener('change', () => {
                        const file = fileInput.files[0]
                        if (!file) return
                        const reader = new FileReader()
                        reader.onload = () => { importRequest(reader.result); fileInput.value = '' }
                        reader.readAsText(file)
                    })
                    const b = h('button', { className: 'adm-btn adm-btn--ghost', type: 'button' }, '📥 Importar solicitud')
                    b.addEventListener('click', () => fileInput.click())
                    return h('span', { style: 'display:inline-flex' }, b, fileInput)
                })(),
                (() => {
                    const b = h('button', { className: 'adm-btn adm-btn--ghost', type: 'button' }, '⬇️ Exportar businesses.json')
                    b.addEventListener('click', exportBusinesses)
                    return b
                })(),
                (() => {
                    const b = h('button', { className: 'adm-btn adm-btn--ghost', type: 'button' }, '⬇️ Exportar promos.json')
                    b.addEventListener('click', exportPromos)
                    return b
                })(),
                (() => {
                    if (!hasPending) return h('span', {})
                    const b = h('button', { className: 'adm-btn adm-btn--ghost', type: 'button' }, '↩️ Descartar cambios')
                    b.addEventListener('click', discardChanges)
                    return b
                })()
            )
        )
        body.appendChild(exportBar)

        /* Tabs */
        const tabsEl = h('div', { className: 'admin-tabs' })
        const tabs = [
            { id: 'solicitudes', label: '📬 Solicitudes' },
            { id: 'businesses', label: `🏪 Negocios (${businesses.length})` },
            { id: 'promos',     label: `🎁 Promociones (${promos.length})`  },
            { id: 'stats',      label: '📊 Estadísticas' },
            { id: 'guide',      label: '📋 Guía' },
        ]
        tabs.forEach(({ id, label }) => {
            const btn = h('button', {
                className: `admin-tab${activeTab === id ? ' admin-tab--active' : ''}`,
                type: 'button'
            }, label)
            btn.addEventListener('click', () => {
                activeTab = id
                render()
            })
            tabsEl.appendChild(btn)
        })
        body.appendChild(tabsEl)

        /* Contenido del tab */
        const tabContent = h('div', {})
        if (activeTab === 'solicitudes') {
            tabContent.appendChild(c('AdminSolicitudes', {
                onApprove: (sol) => {
                    importRequest(sol)
                }
            }))
        } else if (activeTab === 'businesses') {
            tabContent.appendChild(c('AdminBusinesses', {
                businesses,
                onSave:       bizSave,
                onDelete:     bizDelete,
                onToggle:     bizToggle,
                onViewsBoost: bizViewsBoost,
            }))
        } else if (activeTab === 'stats') {
            tabContent.appendChild(c('AdminStats', { businesses, promos }))
        } else if (activeTab === 'guide') {
            tabContent.appendChild(c('AdminGuide'))
        } else {
            tabContent.appendChild(c('AdminPromos', {
                promos,
                businesses,
                onSave:       promoSave,
                onDelete:     promoDelete,
                onToggle:     promoToggle,
                onViewsBoost: promoViewsBoost,
            }))
        }
        body.appendChild(tabContent)
    }

    loadData()
    return root
})
